import { useContext } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ProjectContext } from '../../../../contexts'
import { TemplateUpdateParams } from '../../../../types'
import { Alert, Button } from '../../../../ui'
import { PreferencesContext } from '../../../../ui/PreferencesContext'
import { formatDate, parseUtcDate } from '../../../../utils'
import DateTimeField from '../../launch/DateTimeField'

export const useExpiresAtLabel = () => {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [preferences] = useContext(PreferencesContext)

    return (value?: string, inUserTimezone?: boolean) => {
        if (!value) return t('expires_never')
        const date = parseUtcDate(value)
        if (!date) return undefined
        const timezone = project.timezone
        return inUserTimezone
            ? t('expires_at_read_back_user', {
                time: formatDate(preferences, date, 'p', timezone),
                date: formatDate(preferences, date, 'EEE, MMM d', timezone),
            })
            : t('expires_at_read_back', {
                date: formatDate(preferences, date, 'EEE, MMM d, p', timezone),
                timezone,
            })
    }
}

export const ExpiresAtField = ({ form }: { form: UseFormReturn<TemplateUpdateParams, any> }) => {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const expiresAtLabel = useExpiresAtLabel()
    const value: string | undefined = form.watch('data.expires_at')
    const inUserTimezone: boolean | undefined = form.watch('data.expires_in_user_timezone')
    const label = expiresAtLabel(value, inUserTimezone)

    return <>
        <label>
            <span>{t('expires_at')}</span>
            <span className="label-subtitle">{t('expires_at_desc')}</span>
        </label>
        {!label && <Alert
            variant="warn"
            size="small"
            title={t('expires_at_unreadable')}
            body={t('expires_at_unreadable_desc', { value })} />}
        <DateTimeField
            control={form.control}
            name="data.expires_at"
            label={t('expires_at')}
            dateLabel={t('date')}
            timeLabel={t('time')}
            notice={inUserTimezone
                ? t('expires_at_user_timezone_notice')
                : t('expires_at_project_timezone_notice', { timezone: project.timezone })} />
        <p>
            <strong>{label ?? t('expires_never')}</strong>
            {value && <>
                &nbsp;
                <Button
                    variant="plain"
                    size="small"
                    onClick={() => form.setValue('data.expires_at', undefined, { shouldDirty: true })}>
                    {t('clear')}
                </Button>
            </>}
        </p>
    </>
}
